import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProductById,
  addToBuyCart,
  addToRentCart
} from '../../redux/slices/cartSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useGetProductQuery, useAddCartItemMutation, useReplaceRentalPeriodMutation, useCreateFromListingMutation, useCheckListingInClosetQuery, useGetVtoResultsByGarmentQuery } from '../../redux/apis';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import VirtualTryOnModal from '../../components/closet/VirtualTryOnModal';
import ProductGallery from './components/ProductGallery';
import ProductInfo from './components/ProductInfo';
import ProductActions from './components/ProductActions';
import ErrorMessage from '../../components/common/ErrorMessage';
import BackButton from '../../components/common/BackButton';
import { redirectToCart } from '../../utils/navigation';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import AuthRequiredModal from '../../components/common/AuthRequiredModal';

import ProductReviewsDisplay from './components/ProductReviewsDisplay';
import DigitalLicenseSection from './components/DigitalLicenseSection';
import ContentCredentials from './components/ContentCredentials';
import LicensePurchaseModal from '../../components/licensing/LicensePurchaseModal';
import { getProductImageUrl } from '../../components/closet/VTOResultView';

const ProductPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const currentUser = useSelector(selectCurrentUser);
  const [addCartItem] = useAddCartItemMutation();
  const [replaceRentalPeriod] = useReplaceRentalPeriodMutation();


  // State for rental period replacement modal
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [rentalChoice, setRentalChoice] = useState(null);

  // State for license purchase modal
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [licenseProduct, setLicenseProduct] = useState(null);
  const vtoModalRef = useRef(null);

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authActionType, setAuthActionType] = useState('continue');

  // Helper to check auth before an action
  const requireAuth = (actionLabel) => {
    if (!currentUser) {
      setAuthActionType(actionLabel);
      setShowAuthModal(true);
      return false;
    }
    return true;
  };

  // Fetch Product
  const {
    data: product,
    isLoading: productLoading,
    error: productError
  } = useGetProductQuery(id, {
    refetchOnMountOrArgChange: false,
    skip: !id
  });

  // VTO State and Logic
  const [createFromListing, { isLoading: isVTOAdding }] = useCreateFromListingMutation();
  const [isVTOModalOpen, setIsVTOModalOpen] = useState(false);
  const [closetClothesId, setClosetClothesId] = useState(null);

  const isAuthenticated = !!currentUser;
  const { data: closetCheck } = useCheckListingInClosetQuery(product?.id, {
    skip: !isAuthenticated || !product?.id
  });

  const effectiveClosetClothesId = closetClothesId || closetCheck?.closetClothesId;
  const { data: existingVtoResults = [] } = useGetVtoResultsByGarmentQuery(
    effectiveClosetClothesId,
    { skip: !isAuthenticated || !effectiveClosetClothesId }
  );
  const hasExistingTryOns = existingVtoResults.length > 0;

  const handleOpenVTO = async () => {
    if (!requireAuth('use virtual try-on')) return;
    try {
      const result = await createFromListing({ listingId: product.id }).unwrap();
      setClosetClothesId(result.data?.id || result.closetClothesId);
      setIsVTOModalOpen(true);
    } catch (error) {
      console.error('Error adding to closet:', error);
      setIsVTOModalOpen(true);
    }
  };

  const handleMessageMerchant = async () => {
    if (!requireAuth('message the seller')) return;

    // Check both merchant and users_permissions_user fields for seller info
    const seller = product?.merchant || product?.users_permissions_user;

    if (!product || !seller) {
      toast.error('Cannot find merchant information');
      return;
    }

    try {
      // Navigate to the product-specific message page
      history.push(`/message/product/${product.id}`);
    } catch (error) {
      console.error('Error navigating to message page:', error);
      toast.error('Could not open chat with seller');
    }
  };

  // Format rental period for display
  const formatRentalPeriod = (period) => {
    if (!period) return 'Unknown period';

    const number = period.number || 1;
    const option = period.option || 'day';

    return `${number} ${option}${number > 1 ? 's' : ''}`;
  };

  const handleAddToCart = async (cartData) => {

    if (!requireAuth('add items to your cart')) return;

    try {
      // Check if this is a virtual product
      const isVirtual = product.productType === 'virtual';

      // Validate product stock before adding to cart - skip for virtual products
      if (!isVirtual) {
        const requestedQuantity = cartData.quantity || 1;
        const availableStock = parseInt(product.stock);

        if (availableStock === 0) {
          toast.error(`Sorry, ${product.name} is out of stock`);
          return;
        }

        // Check if this would exceed the available stock
        if (availableStock < requestedQuantity) {
          toast.error(`Sorry, only ${availableStock} units available`);
          return;
        }
      }

      const cartItem = {
        product: product.id,
        quantity: isVirtual ? 1 : (cartData.quantity || 1),
        type: cartData.type || 'buy',
      };


      // Add rental period data if it's a rental
      if (cartData.type === 'rent' && cartData.rentalPeriod) {
        cartItem.rentalPeriod = cartData.rentalPeriod;
      }

      // Add delivery method if provided and the product is not virtual
      if (product.productType !== 'virtual') {
        // Always include a delivery method, defaulting to 'shipping' if not specified
        cartItem.deliveryMethod = cartData.deliveryMethod || cartData.deliveryOption || 'shipping';
      }

      // If this product has a merchant, add it to cart item
      if (product.users_permissions_user) {
        cartItem.merchant = product.users_permissions_user.id;
      }

      // Add any product options that were selected
      if (cartData.productOptions && cartData.productOptions.length > 0) {
        cartItem.productOptions = cartData.productOptions;
      }

      // Add custom note if provided
      if (cartData.customNote) {
        cartItem.customNote = cartData.customNote;
      }

      // Special handling for virtual products
      if (isVirtual) {
        cartItem.isVirtual = true;
      }


      // Add to cart
      const result = await addCartItem(cartItem).unwrap();

      // Handle the case where the same rental period is already in cart
      if (result && result.status === 'already_in_cart') {
        toast.success(result.message || `${product.name} already in your cart.`);

        // Optionally navigate to cart
        if (cartData.navigateToCart) {
          redirectToCart(cartData.type === 'rent');
        }
        return;
      }

      // Handle the case where the user already has this product with a different rental period
      if (result && result.status === 'choice_required') {
        // Store choice data for the modal
        setRentalChoice({
          options: result.options,
          itemIndex: result.itemIndex,
          newItem: result.newItem,
          navigateToCart: cartData.navigateToCart
        });

        // Show the modal
        setShowRentalModal(true);
        return;
      }

      // Normal success case
      if (result) {
        const message = `${product.name} added to your cart!`;
        toast.success(message);
      }

      // Optionally navigate to cart
      if (cartData.navigateToCart) {
        redirectToCart(cartData.type === 'rent');
      }
    } catch (error) {
      console.error('Add to cart error:', error);

      // Handle specific error messages from the server
      if (error.data && error.data.message) {
        if (error.data.message.includes('Not enough quantity available')) {
          toast.error(`Sorry, not enough stock available for ${product.name}`);
        } else {
          toast.error(error.data.message);
        }
      } else {
        toast.error('Failed to add item to cart. Please try again.');
        console.error('Add to cart error details:', error);
      }
    }
  };

  // Handle rental period replacement choice
  const handleRentalChoice = async (action) => {
    try {
      // Only handle replace action now
      await replaceRentalPeriod({
        itemIndex: rentalChoice.itemIndex,
        rentalPeriod: rentalChoice.newItem.rentalPeriod
      }).unwrap();

      toast.success(`Rental period updated for ${product.name}`);

      // Close the modal
      setShowRentalModal(false);

      // Navigate to cart if that was the original intent
      if (rentalChoice.navigateToCart) {
        redirectToCart(true);
      }
    } catch (error) {
      console.error('Error handling rental choice:', error);
      toast.error('Failed to update cart. Please try again.');
    }
  };

  if (productLoading) return <LoadingSpinner />;
  if (productError) {
    console.error('Product fetch error:', productError);
    return <ErrorMessage message="We couldn't load this product. Please try again later." />;
  }
  if (!product) return <ErrorMessage message="Product not found" />;

  // Check if this is a virtual fashion item
  const isVirtual = product.productType === 'virtual';

  return (
    <div className="min-h-screen">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BackButton className="mb-6" />

        {/* Responsive Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 xl:gap-20 2xl:gap-24 md:items-start">
          {/* Left column - Image Gallery */}
          <div className="w-full flex justify-center md:sticky md:top-24">
            <div className="w-full max-w-2xl">
              <ProductGallery
                images={product.photos}
                generatedProductPhoto={product.generatedProductPhoto}
                isLoading={productLoading}
                className=""
                coverImageIndex={product.coverImageIndex}
              />
            </div>
          </div>

          {/* Right column - Product Info */}
          <div className="w-full mt-10 px-4 sm:px-0 sm:mt-16 md:mt-0 flex justify-center">
            <div className="w-full max-w-2xl space-y-4">
              <ProductActions
                product={product}
                onAddToCart={handleAddToCart}
                onMessageMerchant={handleMessageMerchant}
              />
              <DigitalLicenseSection
                product={product}
                onOpenVTO={handleOpenVTO}
                isVTOAdding={isVTOAdding}
                hasExistingTryOns={hasExistingTryOns}
                onPurchaseLicense={(p) => {
                  setLicenseProduct(p);
                  setIsLicenseModalOpen(true);
                }}
              />
              <ProductInfo
                product={product}
                onMessageMerchant={handleMessageMerchant}
              />
              {/* <ContentCredentials product={product} /> */}

              {/* ADDED: Display Product Reviews */}
              <ProductReviewsDisplay productId={product.id} />
            </div>
          </div>
        </div>
      </div>

      {!isVirtual && (
        <Modal
          title="Same Item, Different Rental Period"
          isOpen={showRentalModal}
          onClose={() => setShowRentalModal(false)}
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              You already have this item with a different rental period in your cart.
            </p>

            {rentalChoice && (
              <div className="bg-gray-50 p-4 rounded-md space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Existing rental:</span>
                  <span className="font-medium">
                    {rentalChoice.options && rentalChoice.options[0] &&
                      formatRentalPeriod(rentalChoice.options[0].existingPeriod)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">New rental:</span>
                  <span className="font-medium">
                    {rentalChoice.options && rentalChoice.options[0] &&
                      formatRentalPeriod(rentalChoice.options[0].newPeriod)}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-center mt-4">
              <Button
                variant="primary"
                className="w-full max-w-sm"
                onClick={() => handleRentalChoice('replace')}
              >
                Replace Rental Period
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <VirtualTryOnModal
        isOpen={isVTOModalOpen}
        onClose={() => setIsVTOModalOpen(false)}
        product={product}
        closetClothesId={effectiveClosetClothesId}
        onPurchaseLicense={(p) => {
          setLicenseProduct(p);
          setIsLicenseModalOpen(true);
        }}
        ref={vtoModalRef}
      />

      {/* License Purchase Modal */}
      <LicensePurchaseModal
        isOpen={isLicenseModalOpen}
        onClose={() => setIsLicenseModalOpen(false)}
        listing={licenseProduct ? {
          id: licenseProduct.id,
          itemType: 'product',
          name: licenseProduct.name,
          licensingPrice: licenseProduct.licensingPrice,
          currency: licenseProduct.currency || 'USD',
          coverImage: getProductImageUrl(licenseProduct),
          designerName: (() => {
            const u = licenseProduct.users_permissions_user;
            if (!u) return '';
            return (u.firstname || u.lastname)
              ? `${u.firstname || ''} ${u.lastname || ''}`.trim()
              : u.username;
          })()
        } : null}
        onSuccess={() => {
          toast.success('License purchased successfully!');
          // Notify VTO modal to refresh its data immediately
          if (vtoModalRef.current?.onLicensePurchased) {
            vtoModalRef.current.onLicensePurchased();
          }
        }}
      />

      {/* Auth Required Modal */}
      <AuthRequiredModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        message={`Please log in to ${authActionType}`}
        actionType={authActionType}
      />
    </div >
  );
};

export default ProductPage;